import { TypeSystem } from "../../support/type-system.support";
import { ApplicationUser } from "./application-user.model";
import { Campaign } from "./campaign.model";
import { ObservationDebtType } from "./observation-debt-type.enum";
import { OrderBasket } from "./order-basket.model";
import { OrderGiftChoice } from "./order-gift-choice.model";
import { OrderItem } from "./order-item.model";
import { OrderStatus } from "./order-status.enum";
import { OrderTracking } from "./order-tracking.model";
import { ResellerTransaction } from "./reseller-transaction.model";
import { TemplateModel } from "./template-model.model";

	/**
	 * @description
	 *  Modelo de un pedido.
	 *
	 * @see gigot-backend/Models/Orders/Order.cs
	 */

export class Order {

	public applicationUser: ApplicationUser;
	public applicationUserId: string;
	public area: number;
	public campaign: Campaign;
	public campaignId: number;
	public control: boolean;
	public createdBy: ApplicationUser;
	public createdById: string;
	public excemptTaxAmount: number;
	public grossIncomeLabel: string;
	public grossIncomePercentageAmount: number;
	public grossIncomeTaxAmount: number;
	public id: number;
	public invoiceTotalAmount: number;
	public invoiceTypeLabel: string;
	public invoicingDate: Date;
	public ivaNoCatLabel: string;
	public ivaNoCatTaxAmount: number;
	public lastInvoicingMessage: string;
	public legacyOrderId: number;
	public netAmount: number;
	public observationDebtType: ObservationDebtType;
	public orderBaskets: OrderBasket[];
	public orderDate: Date;
	public orderGiftChoices: OrderGiftChoice[];
	public orderItems: OrderItem[];
	public orderNumber: string;
	public orderStatus: OrderStatus;
	public orderTracking: OrderTracking[];
	public piqueoBoxes: number;
	public previousOrder: Order;
	public resellerTransaction: ResellerTransaction;
	public shippingAmount: number;
	public shippingLabel: string;
	public templateModel: TemplateModel;
	public vatAmount: number;
	public zone: number;

	/**
	 * @description
	 *	Construye un pedido con una semántica vacía. Nótese que no es lo mismo
	 *	que construir un pedido con valores por defecto. Las reglas generales
	 *	de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Un pedido vacío.
	 */
	public static empty(): Order {
		const order: Order = new Order();
		order.applicationUser = ApplicationUser.empty();
		order.applicationUserId = "";
		order.area = undefined;
		order.campaign = Campaign.empty();
		order.campaignId = undefined;
		order.control = false;
		order.createdBy = ApplicationUser.empty();
		order.createdById = "";
		order.excemptTaxAmount = undefined;
		order.grossIncomeLabel = "";
		order.grossIncomePercentageAmount = undefined;
		order.grossIncomeTaxAmount = undefined;
		order.id = undefined;
		order.invoiceTotalAmount = undefined;
		order.invoiceTypeLabel = "";
		order.invoicingDate = undefined;
		order.ivaNoCatLabel = "";
		order.ivaNoCatTaxAmount = undefined;
		order.lastInvoicingMessage = "";
		order.legacyOrderId = undefined;
		order.netAmount = undefined;
		order.observationDebtType = undefined;
		order.orderBaskets = [];
		order.orderDate = undefined;
		order.orderGiftChoices = [];
		order.orderItems = [];
		order.orderNumber = "";
		order.orderStatus = undefined;
		order.orderTracking = [];
		order.piqueoBoxes = undefined;
		order.previousOrder = undefined;
		order.resellerTransaction = ResellerTransaction.empty();
		order.shippingAmount = undefined;
		order.shippingLabel = "";
		order.templateModel = TemplateModel.empty();
		order.vatAmount = undefined;
		order.zone = undefined;
		return order;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicOrder
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicOrder: Order): Order {
		if (!anemicOrder) return undefined;
		const order: Order = Order.empty();
		order.applicationUser = ApplicationUser.empty();
		order.applicationUserId = anemicOrder.applicationUserId;
		order.area = anemicOrder.area;
		order.campaign = Campaign.from(anemicOrder.campaign);
		order.campaignId = anemicOrder.campaignId;
		order.control = anemicOrder.control;
		order.createdBy = ApplicationUser.from(anemicOrder.applicationUser);
		order.createdById = anemicOrder.createdById;
		order.excemptTaxAmount = anemicOrder.excemptTaxAmount;
		order.grossIncomeLabel = anemicOrder.grossIncomeLabel;
		order.grossIncomePercentageAmount = anemicOrder.grossIncomePercentageAmount;
		order.grossIncomeTaxAmount = anemicOrder.grossIncomeTaxAmount;
		order.id = anemicOrder.id;
		order.invoiceTotalAmount = anemicOrder.invoiceTotalAmount;
		order.invoiceTypeLabel = anemicOrder.invoiceTypeLabel;
		order.invoicingDate = new Date(anemicOrder.invoicingDate);
		order.ivaNoCatLabel = anemicOrder.ivaNoCatLabel;
		order.ivaNoCatTaxAmount = anemicOrder.ivaNoCatTaxAmount;
		order.lastInvoicingMessage = anemicOrder.lastInvoicingMessage;
		order.legacyOrderId = anemicOrder.legacyOrderId;
		order.netAmount = anemicOrder.netAmount;
		order.observationDebtType = anemicOrder.observationDebtType;
		order.orderBaskets = TypeSystem.orElse(anemicOrder.orderBaskets, [])
			.map(orderBasket => OrderBasket.from(orderBasket));
		order.orderDate = new Date(anemicOrder.orderDate);
		order.orderGiftChoices = TypeSystem.orElse(anemicOrder.orderGiftChoices, [])
			.map(orderGiftChoice => OrderGiftChoice.from(orderGiftChoice));
		order.orderItems = TypeSystem.orElse(anemicOrder.orderItems, [])
			.map(orderItem => OrderItem.from(orderItem));
		order.orderNumber = anemicOrder.orderNumber;
		order.orderStatus = anemicOrder.orderStatus;
		order.orderTracking = TypeSystem.orElse(anemicOrder.orderTracking, [])
			.map(orderTracking => OrderTracking.from(orderTracking));
		order.piqueoBoxes = anemicOrder.piqueoBoxes;
		order.previousOrder = Order.from(anemicOrder.previousOrder);
		order.resellerTransaction = ResellerTransaction.from(anemicOrder.resellerTransaction);
		order.shippingAmount = anemicOrder.shippingAmount;
		order.shippingLabel = anemicOrder.shippingLabel;
		order.templateModel = TemplateModel.from(anemicOrder.templateModel);
		order.vatAmount = anemicOrder.vatAmount;
		order.zone = anemicOrder.zone;
		return order;
	}
}
